import React, { useState, useEffect } from 'react'
import { number, string } from 'prop-types'
import { css } from '@emotion/core'
import {
  ZionForm,
  Button,
  Autosuggest,
  DialogOverlay,
  DialogOverlayContent,
  InputRow,
  useOverlay,
  Body2,
} from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { getAnonSession } from '@fs/zion-session'
import { useTranslation } from 'react-i18next'
import { useRegionLanguage } from '../../hooks/hooks'
import { useDSCDispatch } from '../../../../state/DSCStore'

const separatorStyle = css`
  font-size: 12px;
  text-align: center;
  max-width: 100%;
  color: #acacac;
  position: relative;
  ::before {
    content: '';
    display: block;
    width: 220px;
    height: 1px;
    background: #acacac;
    position: absolute;
    left: 0;
    top: 50%;
    z-index: -2;
  }
  ::after {
    content: '';
    display: block;
    width: 220px;
    height: 1px;
    background: #acacac;
    position: absolute;
    right: 0;
    top: 50%;
    z-index: -2;
  }
  span {
    background: #fff;
  }
`

export default function LanguageWarningDialog({ language, region, year }) {
  const [isValidLangOption, setValidLangOption] = useState(true)
  const user = useUser()
  const overlay = useOverlay()
  const { t } = useTranslation()
  const countryLanguageOptions = useRegionLanguage(t)
  const dispatch = useDSCDispatch()

  useEffect(() => {
    if (region && language && countryLanguageOptions.length !== 0) {
      const foundValidLangOption = countryLanguageOptions.some((option) => option.key === `${region}_${language}`)
      if (!foundValidLangOption) {
        setValidLangOption(false)
      }
    }
  }, [countryLanguageOptions, language, region])

  useEffect(() => {
    if (!isValidLangOption) {
      overlay.open()
    }
    // eslint-disable-next-line
  }, [isValidLangOption])

  const validate = (values) => {
    const errors = {}
    if (!values.modifiedValue) {
      errors.modifiedValue = t(
        'aam-landing-validation-select-country-language',
        'Please select a valid country and language'
      )
    }

    return errors
  }

  const passToGlobal = (values, formikBag) => {
    if (user) {
      const names = user.contactName.split(' ')
      values.firstName = names[0]
      values.lastName = names[1]
      values.birthYear = year
    }
    values.languageRegion = values.modifiedValue
    formikBag.resetForm()
    getAnonSession()
      .then(() => {
        dispatch({
          type: 'POPULATE_NONSTANDARD_USER',
          data: { nonstandardUser: { ...values } },
        })
      })
      .then(() => {
        overlay.close()
        window.location.reload()
      })
  }

  const suppressAutocomplete = (event) => {
    event.target.setAttribute('autocomplete', 'disabled')
  }

  return (
    <DialogOverlay {...overlay} headingText={t('aam.country-language-oops')}>
      <DialogOverlayContent>
        <Body2>
          <span css={{ color: '#b81f1e' }}>
            {t(`global.countries.country${region.toUpperCase()}`)} / {t(`global.countries.language.${language}`)}
          </span>
        </Body2>
        <Body2>{t('aam.country-language-warning-message')}</Body2>
        <div css={separatorStyle}>
          <span>{t('aam.country-language-separator', 'OR')}</span>
        </div>
        <Body2>{t('aam.country-language-warning-ignore-message')}</Body2>
        <ZionForm onSubmit={passToGlobal} validate={validate}>
          {({ isValid, ...formProps }) => (
            <>
              <Autosuggest
                suggestions={countryLanguageOptions}
                value={formProps.values.modifiedValue}
                name="modifiedValue"
                inputProps={{
                  onFocus: suppressAutocomplete,
                }}
                placeholder={t('aam-landing-validation-select-country-language')}
                highlightMatchingText
              />
              <InputRow>
                <Button type="submit" external="true" emphasis="high" disabled={!isValid}>
                  {t('common-ui:submit.action', 'Submit')}
                </Button>
                <Button onClick={() => overlay.close()}> {t('common-ui:cancel.action', 'Cancel')}</Button>
              </InputRow>
            </>
          )}
        </ZionForm>
      </DialogOverlayContent>
    </DialogOverlay>
  )
}

LanguageWarningDialog.propTypes = {
  language: string,
  region: string,
  year: number,
}
